import { Button, ColorPicker, Input, Select, Space } from 'antd'
import { AggregationColor } from 'antd/es/color-picker/color'
import { NiiSourcesZoneParams } from 'features/libra/stepComponents/niiReport/niiReportApi'
import { FC, useEffect, useState } from 'react'
import styled from 'styled-components'

const EditPanel = styled.div`
  padding: 16px;
  background-color: ${({ theme }) => theme.forecast.background3};
  border-top: 1px solid ${({ theme }) => theme.forecast.border};
`

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 16px;
`

const Label = styled.label`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.forecast.secondaryText};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const FieldRow = styled.div`
  display: flex;
  gap: 16px;
  align-items: flex-end;
`

const INSPECTION_METHODS = [
  { value: 'VT', label: 'Visual Testing (VT)' },
  { value: 'UT', label: 'Ultrasonic Testing (UT)' },
  { value: 'PT', label: 'Penetrant Testing (PT)' },
  { value: 'MT', label: 'Magnetic Testing (MT)' },
  { value: 'RT', label: 'Radiographic Testing (RT)' },
  { value: 'ET', label: 'Eddy Current Testing (ET)' },
]

const MATERIALS = [
  { value: 'carbon_steel', label: 'Carbon Steel' },
  { value: 'stainless_steel', label: 'Stainless Steel' },
  { value: 'aluminum', label: 'Aluminum' },
  { value: 'copper', label: 'Copper' },
  { value: 'titanium', label: 'Titanium' },
  { value: 'other', label: 'Other' },
]

export const ZoneEditPanel: FC<{
  zone: NiiSourcesZoneParams
  onSave: (updates: Partial<NiiSourcesZoneParams>) => void
  onCancel: () => void
}> = ({ zone, onSave, onCancel }) => {
  const [name, setName] = useState(zone.title || '')
  const [inspectionMethod, setInspectionMethod] = useState<string>()
  const [material, setMaterial] = useState<string>()
  const [color, setColor] = useState('#3B82F6') // Default blue

  useEffect(() => {
    setName(zone.title || '')
    setInspectionMethod(zone.inspection_method)
    setMaterial(zone.material)
    setColor(zone.color || '#3B82F6')
  }, [zone])

  const handleColorChange = (value: AggregationColor) => {
    setColor(value.toHexString())
  }

  const handleSave = () => {
    const updates: Partial<NiiSourcesZoneParams> = {
      title: name,
      inspection_method: inspectionMethod,
      material: material,
      color: color,
    }
    onSave(updates)
  }

  return (
    <EditPanel>
      <FieldGroup>
        <Label>Zone Name</Label>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder='Enter zone name'
          size='small'
        />
      </FieldGroup>

      <FieldRow>
        <FieldGroup style={{ flex: 1 }}>
          <Label>Inspection Method</Label>
          <Select
            value={inspectionMethod}
            onChange={setInspectionMethod}
            placeholder='Select method'
            options={INSPECTION_METHODS}
            allowClear
            size='small'
          />
        </FieldGroup>

        <FieldGroup style={{ flex: 1 }}>
          <Label>Material</Label>
          <Select
            value={material}
            onChange={setMaterial}
            placeholder='Select material'
            options={MATERIALS}
            allowClear
            size='small'
          />
        </FieldGroup>

        <FieldGroup>
          <Label>Color</Label>
          <ColorPicker
            value={color}
            onChange={handleColorChange}
            size='small'
          />
        </FieldGroup>
      </FieldRow>

      <Space size='small' style={{ marginTop: '12px' }}>
        <Button type='primary' size='small' onClick={handleSave}>
          Save
        </Button>
        <Button size='small' onClick={onCancel}>
          Cancel
        </Button>
      </Space>
    </EditPanel>
  )
}
